import { z } from "zod";

/**
 * 🔴 US-E6.F3-0910 (@UC0057 @UC9588 · QĐ người dùng 06/10/2026) — BẰNG CHỨNG NỘP QUỸ: tối đa MỘT file, ảnh hoặc PDF.
 *
 * File thuộc về HOẠT ĐỘNG (lần nộp đó), không thuộc từng kỳ hay từng người. Activity là projection chỉ-đọc ⇒ khoá file
 * nằm ở BẢN GHI GỐC: lời khai ⇒ `payment_confirmations.proofUrl`; O/A/T tự ghi nhận ⇒ `ledger_transactions.attachmentUrl`
 * (đặt lúc INSERT — bảng append-only). Không bảng mới, không migration.
 *
 * Upload HAI BƯỚC (khuôn `disputes/:id/evidence/upload`): `POST /funds/:id/contribution-evidence` trả KHOÁ do SERVER
 * sinh; các đường ghi nhận mang `evidenceKey`. Khoá gồm quỹ + người upload ⇒ server kiểm được "đúng quỹ, đúng người"
 * mà không cần bảng phụ — không ai gắn được file của người khác vào hoạt động của mình.
 *
 * File này CHỈ phụ thuộc `zod`: năm contract ghi nhận import nó, không được tạo vòng import.
 */

/** Tên field multipart của bước upload. */
export const CONTRIBUTION_EVIDENCE_FILE_FIELD = "file";

/** Vùng chứa riêng trên R2 (tách `proofs/` của lời khai 1 kỳ cũ và `dispute-evidence/`). */
export const CONTRIBUTION_EVIDENCE_KEY_PREFIX = "contribution-evidence";

/** PDF chỉ được nhận ở bằng chứng nộp quỹ — tranh chấp / biên lai khoản chi vẫn chỉ ảnh. */
export const CONTRIBUTION_EVIDENCE_PDF_CONTENT_TYPE = "application/pdf";

export const EVIDENCE_INVALID_FORMAT_MESSAGE =
  "Định dạng tệp không hỗ trợ. Chỉ chấp nhận ảnh JPEG, PNG, WebP hoặc tệp PDF.";
export const EVIDENCE_MISSING_FILE_MESSAGE = "Chưa chọn tệp bằng chứng.";
export const EVIDENCE_UPLOAD_FAILED_MESSAGE = "Tải bằng chứng thất bại. Thử lại hoặc bỏ đính kèm.";
export const EVIDENCE_KEY_NOT_OWNED_MESSAGE = "Tệp bằng chứng không hợp lệ cho lần nộp này.";
/** Gửi cả ảnh multipart `proof` (đường cũ) lẫn `evidenceKey` ⇒ không biết lấy cái nào. */
export const EVIDENCE_BOTH_SOURCES_MESSAGE = "Chỉ đính kèm MỘT tệp bằng chứng.";

/**
 * Mã lỗi ỔN ĐỊNH (problem+json `code`) — client map `code` → câu hiển thị, không đọc `detail`. Bộ lọc lỗi mặc định đổi
 * 413/415 thành `ERROR` chung ⇒ đường bằng chứng tự gắn mã để mobile nói đúng "quá lớn" / "sai định dạng".
 */
export const CONTRIBUTION_EVIDENCE_ERROR_CODE = {
  TOO_LARGE: "EVIDENCE_TOO_LARGE",
  UNSUPPORTED_FORMAT: "EVIDENCE_UNSUPPORTED_FORMAT",
  MISSING_FILE: "EVIDENCE_MISSING_FILE",
  UPLOAD_FAILED: "EVIDENCE_UPLOAD_FAILED",
  /** `evidenceKey` không phải của CHÍNH người gọi trong CHÍNH quỹ này. */
  KEY_NOT_OWNED: "EVIDENCE_KEY_NOT_OWNED",
  /** Gửi cả multipart `proof` (đường cũ) lẫn `evidenceKey`. */
  BOTH_SOURCES: "EVIDENCE_BOTH_SOURCES",
} as const;

export const EVIDENCE_CONTENT_KIND = { IMAGE: "image", PDF: "pdf" } as const;
export const evidenceContentKindSchema = z.enum([EVIDENCE_CONTENT_KIND.IMAGE, EVIDENCE_CONTENT_KIND.PDF]);
export type EvidenceContentKind = z.infer<typeof evidenceContentKindSchema>;

const UUID = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const IMAGE_EXTENSIONS = ["jpg", "png", "webp"] as const;
const PDF_EXTENSION = "pdf";

/** `contribution-evidence/<fundId>/<uploaderUserId>/<uuid>.<ext>` — đúng dạng `R2ProofStorage` sinh. */
export const CONTRIBUTION_EVIDENCE_KEY_PATTERN = new RegExp(
  `^${CONTRIBUTION_EVIDENCE_KEY_PREFIX}/${UUID}/${UUID}/${UUID}\\.(${[...IMAGE_EXTENSIONS, PDF_EXTENSION].join("|")})$`,
);

/** Khoá file trên dây — KHÔNG phải URL (chặn client gắn một URL tuỳ ý vào hoạt động). */
export const evidenceKeySchema = z
  .string()
  .max(200)
  .regex(CONTRIBUTION_EVIDENCE_KEY_PATTERN, "Khoá tệp bằng chứng không hợp lệ");

/** Tiền tố của mọi khoá mà `uploaderUserId` upload vào quỹ `fundId`. */
export function contributionEvidenceKeyPrefix(fundId: string, uploaderUserId: string): string {
  return `${CONTRIBUTION_EVIDENCE_KEY_PREFIX}/${fundId}/${uploaderUserId}/`;
}

/** 🔴 Khoá có đúng là của CHÍNH người này, trong CHÍNH quỹ này không — cổng chống gắn file người khác. */
export function isEvidenceKeyOwnedBy(key: string, fundId: string, uploaderUserId: string): boolean {
  return (
    CONTRIBUTION_EVIDENCE_KEY_PATTERN.test(key) &&
    key.startsWith(contributionEvidenceKeyPrefix(fundId.toLowerCase(), uploaderUserId.toLowerCase()))
  );
}

/**
 * Loại file suy từ ĐUÔI khoá (server tự sinh đuôi theo loại sniff được — tin được). Không cần cột mime.
 * Khoá cũ của lời khai 1 kỳ (`proofs/…jpg`) vẫn ra `image`.
 */
export function evidenceContentKindOfKey(key: string | null | undefined): EvidenceContentKind | null {
  if (!key) return null;
  const ext = /\.([a-z0-9]+)$/i.exec(key)?.[1]?.toLowerCase();
  if (ext === PDF_EXTENSION) return EVIDENCE_CONTENT_KIND.PDF;
  if (ext !== undefined && (IMAGE_EXTENSIONS as readonly string[]).includes(ext)) return EVIDENCE_CONTENT_KIND.IMAGE;
  return null;
}

/** Response bước upload: KHOÁ + loại. `.strict()` ⇒ server lỡ trả URL thì NỔ ở biên. */
export const uploadContributionEvidenceResponseSchema = z
  .object({
    evidenceKey: evidenceKeySchema,
    contentKind: evidenceContentKindSchema,
  })
  .strict();
export type UploadContributionEvidenceResponse = z.infer<typeof uploadContributionEvidenceResponseSchema>;

/**
 * File đính kèm ĐỌC LẠI: link presigned TTL ngắn (`PROOF_READBACK_TTL_SECONDS`) + loại + hạn. Ảnh/PDF chuyển khoản =
 * PII ⇒ link sống ngắn, client hết hạn thì đọc lại.
 */
export const evidenceAttachmentSchema = z
  .object({
    url: z.string(),
    contentKind: evidenceContentKindSchema,
    expiresAt: z.string().datetime().nullable(),
  })
  .strict();
export type EvidenceAttachment = z.infer<typeof evidenceAttachmentSchema>;
